/**
 * TaskDetailDrawer Component - MED-131 Redesign
 * Single scrollable panel (no tabs), enhanced layout
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Button,
  Divider,
  TextInput,
  Textarea,
  Select,
  ActionIcon,
  Loader,
  Center,
  Avatar,
  Box,
  Checkbox,
  Badge,
  Menu,
  Tooltip,
  ScrollArea,
  Breadcrumbs,
  Anchor,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  Edit3,
  Save,
  Plus,
  MoreVertical,
  Calendar,
  MessageSquare,
  Paperclip,
  Check,
  AlertTriangle,
  Trash2,
  ChevronRight,
  X,
  Link as LinkIcon,
} from 'react-feather';
import type {
  TaskDetail,
  TaskWithDetails,
  TaskStatus,
  TaskPriority,
  TaskPhase,
  CreateTaskRequest,
  UpdateTaskRequest,
} from '../../../types';
import { tasksApi } from '../../../services/api/tasks';
import { usersApi, type InternalUser } from '../../../services/api/users';
import {
  TASK_STATUS_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_PHASE_OPTIONS,
} from '../../../config/taskConfig';
import { formatDate, formatDateOnly } from '../../../utils/formatters';
import { isDateOnlyPast, parseDateOnly, toISODateString } from '../../../utils/dateHelpers';
import { RelatedSessionCard } from './RelatedSessionCard';
import { getAvatarColor, getInitials } from '../../../utils/avatarHelpers';
import { getStatusConfig } from '../../../config/taskConfig';
import { copyTaskLink } from '../../../utils/taskLinks';

/**
 * Section header component for consistent styling
 * MED-163: 11px, #888888, uppercase
 */
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Text
      size="11px"
      c="#888888"
      tt="uppercase"
      fw={500}
      style={{ letterSpacing: '0.3px' }}
      mt="md"
    >
      {children}
    </Text>
  );
}

interface TaskDetailDrawerProps {
  cohortId: string;
  taskId: string | null;
  opened: boolean;
  onClose: () => void;
  onUpdated: (updatedTask?: TaskWithDetails) => void;
  onEditTask?: (task: TaskWithDetails) => void;
  cohortName?: string;
  /** MED-868: Optional external map of subtask id → current status. When provided,
   * the drawer renders subtask checkbox state from this map instead of its locally
   * fetched copy, so toggles from the inline dropdown reflect immediately. */
  externalSubtaskStatusMap?: Record<string, TaskStatus>;
  /** MED-868: Notification fired after a subtask toggle persists, so the parent can
   * keep its shared subtask cache and parent-completion count in sync. */
  onSubtaskToggled?: (subtaskId: string, newStatus: TaskStatus, parentTaskId: string) => void;
}

/**
 * Subtask row component
 */
function SubtaskItem({
  subtask,
  cohortId,
  parentTaskId,
  effectiveStatus,
  onUpdated,
  onEdit,
  onView,
  onSubtaskToggled,
}: {
  subtask: TaskWithDetails;
  cohortId: string;
  parentTaskId: string;
  effectiveStatus: TaskStatus;
  onUpdated: () => void;
  onEdit: (subtask: TaskWithDetails) => void;
  onView: (subtaskId: string) => void;
  onSubtaskToggled?: (subtaskId: string, newStatus: TaskStatus, parentTaskId: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleToggleComplete = async (e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const newStatus: TaskStatus = effectiveStatus === 'complete' ? 'not_started' : 'complete';
      await tasksApi.updateTaskStatus(cohortId, subtask.id, newStatus);
      onSubtaskToggled?.(subtask.id, newStatus, parentTaskId);
      onUpdated();
    } catch (error) {
      console.error('Failed to update subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update subtask',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await tasksApi.deleteTask(cohortId, subtask.id);
      onUpdated();
      notifications.show({
        title: 'Success',
        message: 'Subtask deleted',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to delete subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete subtask',
        color: 'red',
      });
    }
  };

  const isComplete = effectiveStatus === 'complete';

  return (
    <Group
      gap="xs"
      p="sm"
      onClick={() => onView(subtask.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: isHovered ? '#F0EEEA' : '#F9F7F4',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
      }}
    >
      <Checkbox
        checked={isComplete}
        onChange={(e) => handleToggleComplete(e)}
        onClick={(e) => e.stopPropagation()}
        disabled={loading}
        color="medleyGreen"
      />
      <Text
        size="13px"
        style={{ flex: 1 }}
        td={isComplete ? 'line-through' : undefined}
        c={isComplete ? '#888888' : '#28463A'}
      >
        {subtask.title}
      </Text>
      {subtask.assignee_name && (
        <Tooltip label={subtask.assignee_name}>
          <Avatar
            size={22}
            radius="xl"
            color={getAvatarColor(subtask.assignee_id || subtask.assignee_name)}
            style={{ fontSize: 10 }}
          >
            {getInitials(subtask.assignee_name)}
          </Avatar>
        </Tooltip>
      )}
      {subtask.due_date && (
        <Text size="xs" c="#888888">
          {formatDateOnly(subtask.due_date)}
        </Text>
      )}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
            <MoreVertical size={14} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<Edit3 size={14} />} onClick={(e) => { e.stopPropagation(); onEdit(subtask); }}>
            Edit
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item color="red" leftSection={<Trash2 size={14} />} onClick={handleDelete}>
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}

export function TaskDetailDrawer({
  cohortId,
  taskId,
  opened,
  onClose,
  onUpdated,
  onEditTask,
  cohortName,
  externalSubtaskStatusMap,
  onSubtaskToggled,
}: TaskDetailDrawerProps) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  // MED-770: Track fetch error for deep-link edge cases (not found / no access)
  const [fetchError, setFetchError] = useState<'not_found' | 'no_access' | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState<UpdateTaskRequest>({});

  // New comment state
  const [newComment, setNewComment] = useState('');
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [addingComment, setAddingComment] = useState(false);

  // New subtask state
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);

  // Subtask editing state (MED-163)
  const [editingSubtask, setEditingSubtask] = useState<TaskWithDetails | null>(null);
  const [subtaskEditForm, setSubtaskEditForm] = useState<UpdateTaskRequest>({});
  const [savingSubtask, setSavingSubtask] = useState(false);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Subtask detail view state (MED-316)
  const [viewingSubtaskId, setViewingSubtaskId] = useState<string | null>(null);
  const [subtaskDetail, setSubtaskDetail] = useState<TaskDetail | null>(null);
  const [loadingSubtask, setLoadingSubtask] = useState(false);

  // Fetch task details
  const fetchTask = useCallback(async () => {
    if (!taskId) return;

    setLoading(true);
    setFetchError(null);
    try {
      const taskData = await tasksApi.getTaskById(cohortId, taskId);

      // MED-753: When the drawer is opened directly on a subtask (from the flattened
      // My Tasks list or a deep link), route through the parent → subtask flow so the
      // user always gets the existing breadcrumb back to the parent.
      if (taskData.parent_task_id) {
        const parentData = await tasksApi.getTaskById(cohortId, taskData.parent_task_id);
        setTask(parentData);
        setEditForm({
          title: parentData.title,
          description: parentData.description || undefined,
          status: parentData.status,
          priority: parentData.priority,
          phase: parentData.phase || undefined,
          due_date: parentData.due_date || undefined,
        });
        setViewingSubtaskId(taskId);
        setSubtaskDetail(taskData);
        return;
      }

      setTask(taskData);
      setEditForm({
        title: taskData.title,
        description: taskData.description || undefined,
        status: taskData.status,
        priority: taskData.priority,
        phase: taskData.phase || undefined,
        due_date: taskData.due_date || undefined,
      });
    } catch (error: unknown) {
      console.error('Failed to fetch task:', error);
      // MED-770: Classify error for deep-link edge cases
      // The API client enhances errors with a .status property
      const status = (error as { status?: number })?.status;
      if (status === 404) {
        setFetchError('not_found');
      } else if (status === 403) {
        setFetchError('no_access');
      } else {
        setFetchError('not_found');
        notifications.show({
          title: 'Error',
          message: 'Failed to load task details',
          color: 'red',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [cohortId, taskId]);

  useEffect(() => {
    if (opened && taskId) {
      fetchTask();
    } else {
      setTask(null);
      setEditing(false);
      setShowCommentInput(false);
      setNewComment('');
      setEditingSubtask(null);
      setViewingSubtaskId(null);
      setSubtaskDetail(null);
      setFetchError(null);
    }
  }, [opened, taskId, fetchTask]);

  // Fetch users for assignee dropdown (MED-163)
  useEffect(() => {
    if (opened && users.length === 0) {
      setLoadingUsers(true);
      usersApi.listInternal()
        .then(setUsers)
        .catch(console.error)
        .finally(() => setLoadingUsers(false));
    }
  }, [opened, users.length]);

  // Handle opening subtask edit (MED-163)
  const handleEditSubtaskInPane = (subtask: TaskWithDetails) => {
    setEditingSubtask(subtask);
    setSubtaskEditForm({
      title: subtask.title,
      description: subtask.description || undefined,
      assignee_id: subtask.assignee_id || undefined,
      due_date: subtask.due_date || undefined,
      priority: subtask.priority,
    });
  };

  // Handle viewing subtask detail (MED-316)
  const handleViewSubtask = useCallback(async (subtaskId: string) => {
    setViewingSubtaskId(subtaskId);
    setLoadingSubtask(true);
    try {
      const detail = await tasksApi.getTaskById(cohortId, subtaskId);
      setSubtaskDetail(detail);
    } catch (error) {
      console.error('Failed to fetch subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load subtask details',
        color: 'red',
      });
      setViewingSubtaskId(null);
    } finally {
      setLoadingSubtask(false);
    }
  }, [cohortId]);

  const handleBackToParent = () => {
    setViewingSubtaskId(null);
    setSubtaskDetail(null);
  };

  // Handle marking subtask complete from detail view (MED-316)
  const handleSubtaskMarkComplete = async () => {
    if (!subtaskDetail) return;
    const effectiveCurrentStatus =
      externalSubtaskStatusMap?.[subtaskDetail.id] ?? subtaskDetail.status;
    const newStatus: TaskStatus = effectiveCurrentStatus === 'complete' ? 'not_started' : 'complete';
    try {
      await tasksApi.updateTaskStatus(cohortId, subtaskDetail.id, newStatus);
      setSubtaskDetail({ ...subtaskDetail, status: newStatus });
      // MED-868: Notify parent so the shared cache and parent-completion count stay in sync.
      // subtaskDetail.parent_task_id is required when viewing a subtask detail.
      if (subtaskDetail.parent_task_id) {
        onSubtaskToggled?.(subtaskDetail.id, newStatus, subtaskDetail.parent_task_id);
      }
      await fetchTask(); // refresh parent subtask counts
      onUpdated();
    } catch (error) {
      console.error('Failed to update subtask status:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update subtask status',
        color: 'red',
      });
    }
  };

  // Handle save subtask (MED-163)
  const handleSaveSubtask = async () => {
    if (!editingSubtask) return;

    setSavingSubtask(true);
    try {
      await tasksApi.updateTask(cohortId, editingSubtask.id, subtaskEditForm);
      await fetchTask();
      setEditingSubtask(null);
      onUpdated();
      notifications.show({
        title: 'Success',
        message: 'Subtask updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to update subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update subtask',
        color: 'red',
      });
    } finally {
      setSavingSubtask(false);
    }
  };

  // Format users for Select component
  const userOptions = users.map(user => ({
    value: user.id,
    label: user.name,
  }));

  // Handle save
  const handleSave = async () => {
    if (!task) return;

    setSaving(true);
    try {
      await tasksApi.updateTask(cohortId, task.id, editForm);
      await fetchTask();
      setEditing(false);
      onUpdated();
      notifications.show({
        title: 'Success',
        message: 'Task updated successfully',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to update task:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update task',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!task || !newComment.trim()) return;

    setAddingComment(true);
    try {
      await tasksApi.addTaskComment(cohortId, task.id, newComment.trim());
      setNewComment('');
      setShowCommentInput(false);
      await fetchTask();
      onUpdated();
      notifications.show({
        title: 'Success',
        message: 'Comment added',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add comment',
        color: 'red',
      });
    } finally {
      setAddingComment(false);
    }
  };

  // Handle add subtask
  const handleAddSubtask = async () => {
    if (!task || !newSubtaskTitle.trim()) return;

    setAddingSubtask(true);
    try {
      const subtaskData: CreateTaskRequest = {
        title: newSubtaskTitle.trim(),
        task_group_id: task.task_group_id || undefined,
        parent_task_id: task.id,
        phase: task.phase || undefined,
      };
      await tasksApi.createTask(cohortId, subtaskData);
      setNewSubtaskTitle('');
      await fetchTask();
      onUpdated();
      notifications.show({
        title: 'Success',
        message: 'Subtask added',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to add subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add subtask',
        color: 'red',
      });
    } finally {
      setAddingSubtask(false);
    }
  };

  // Handle mark complete
  const handleMarkComplete = async () => {
    if (!task) return;

    const newStatus: TaskStatus = task.status === 'complete' ? 'not_started' : 'complete';
    try {
      await tasksApi.updateTaskStatus(cohortId, task.id, newStatus);
      const updatedTask = { ...task, status: newStatus };
      setTask(updatedTask);
      onUpdated(updatedTask as TaskWithDetails);
      notifications.show({
        title: 'Success',
        message: newStatus === 'complete' ? 'Task marked as complete' : 'Task marked as not started',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to update status:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update status',
        color: 'red',
      });
    }
  };

  // Check if overdue
  const isOverdue =
    task?.due_date &&
    isDateOnlyPast(task.due_date) &&
    task.status !== 'complete' &&
    task.status !== 'cancelled';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={null}
      padding={0}
      withCloseButton={false}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      {loading ? (
        <Center py="xl" style={{ flex: 1 }}>
          <Loader />
        </Center>
      ) : !task ? (
        <Center py="xl" style={{ flex: 1 }}>
          <Stack align="center" gap="xs">
            <AlertTriangle size={28} color="var(--mantine-color-dimmed)" />
            <Text fw={500}>
              {fetchError === 'no_access' ? 'Access Denied' : 'Task Not Found'}
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={280}>
              {fetchError === 'no_access'
                ? 'You do not have access to this task. It may belong to a cohort you are not assigned to.'
                : 'This task could not be found. It may have been deleted or the link may be invalid.'}
            </Text>
            <Button variant="subtle" color="gray" size="sm" onClick={onClose} mt="xs">
              Close
            </Button>
          </Stack>
        </Center>
      ) : editing ? (
        // Edit mode
        <Stack gap="md" p="lg" style={{ flex: 1 }}>
          <Group justify="space-between">
            <Text fw={600} size="lg">Edit Task</Text>
            <Group gap="xs">
              <Button variant="subtle" onClick={() => setEditing(false)} size="sm">
                Cancel
              </Button>
              <Button onClick={handleSave} loading={saving} leftSection={<Save size={14} />} size="sm">
                Save
              </Button>
            </Group>
          </Group>

          <TextInput
            label="Title"
            value={editForm.title || ''}
            onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
          <Textarea
            label="Description"
            value={editForm.description || ''}
            onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
            minRows={3}
          />
          <Group grow>
            <Select
              label="Status"
              data={TASK_STATUS_OPTIONS}
              value={editForm.status}
              onChange={(value) => setEditForm((prev) => ({ ...prev, status: value as TaskStatus }))}
            />
            <Select
              label="Priority"
              data={TASK_PRIORITY_OPTIONS}
              value={editForm.priority}
              onChange={(value) => setEditForm((prev) => ({ ...prev, priority: value as TaskPriority }))}
            />
          </Group>
          <Group grow>
            <Select
              label="Phase"
              data={TASK_PHASE_OPTIONS}
              value={editForm.phase || null}
              onChange={(value) =>
                setEditForm((prev) => ({
                  ...prev,
                  phase: (value as TaskPhase) || undefined,
                }))
              }
              clearable
            />
            <TextInput
              label="Due Date"
              type="date"
              value={editForm.due_date?.split('T')[0] || ''}
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  due_date: e.target.value || undefined,
                }))
              }
            />
          </Group>
        </Stack>
      ) : editingSubtask ? (
        // Subtask edit mode - MED-163
        <Stack gap="md" p="lg" style={{ flex: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<ChevronRight size={12} color="#888888" />}
            styles={{ separator: { marginLeft: 4, marginRight: 4 } }}
          >
            <Anchor
              size="sm"
              c="#28463A"
              onClick={() => setEditingSubtask(null)}
              style={{ cursor: 'pointer' }}
            >
              {task.title}
            </Anchor>
            <Text size="sm" c="#888888">
              {editingSubtask.title}
            </Text>
          </Breadcrumbs>

          <Group justify="space-between">
            <Text fw={600} size="lg" c="#28463A">Edit Subtask</Text>
            <Group gap="xs">
              <ActionIcon variant="subtle" onClick={() => setEditingSubtask(null)}>
                <X size={16} />
              </ActionIcon>
            </Group>
          </Group>

          <TextInput
            label="Title"
            value={subtaskEditForm.title || ''}
            onChange={(e) => setSubtaskEditForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <Textarea
            label="Description"
            value={subtaskEditForm.description || ''}
            onChange={(e) => setSubtaskEditForm((prev) => ({ ...prev, description: e.target.value }))}
            minRows={3}
          />

          <Select
            label="Assignee"
            placeholder="Select assignee"
            data={userOptions}
            clearable
            searchable
            disabled={loadingUsers}
            value={subtaskEditForm.assignee_id || null}
            onChange={(value) => setSubtaskEditForm((prev) => ({ ...prev, assignee_id: value || undefined }))}
          />

          <DateInput
            label="Due Date"
            placeholder="Select date"
            valueFormat="MM/DD/YYYY"
            clearable
            value={parseDateOnly(subtaskEditForm.due_date)}
            onChange={(value) => setSubtaskEditForm((prev) => ({
              ...prev,
              due_date: toISODateString(value ? parseDateOnly(value as string) : null),
            }))}
          />

          <Select
            label="Priority"
            data={TASK_PRIORITY_OPTIONS}
            value={subtaskEditForm.priority}
            onChange={(value) => setSubtaskEditForm((prev) => ({ ...prev, priority: value as TaskPriority }))}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setEditingSubtask(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSubtask}
              loading={savingSubtask}
              leftSection={<Save size={14} />}
            >
              Save Subtask
            </Button>
          </Group>
        </Stack>
      ) : viewingSubtaskId ? (
        // Subtask detail view - MED-316
        loadingSubtask ? (
          <Center py="xl" style={{ flex: 1 }}>
            <Loader />
          </Center>
        ) : !subtaskDetail ? (
          <Center py="xl" style={{ flex: 1 }}>
            <Text c="dimmed">Subtask not found</Text>
          </Center>
        ) : (
          <>
            <ScrollArea style={{ flex: 1 }} p="lg">
              <Stack gap="md">
                {/* Breadcrumbs */}
                <Group justify="space-between" align="flex-start">
                  <Breadcrumbs
                    separator={<ChevronRight size={12} color="#888888" />}
                    styles={{ separator: { marginLeft: 4, marginRight: 4 } }}
                  >
                    <Anchor
                      size="sm"
                      c="#28463A"
                      onClick={handleBackToParent}
                      style={{ cursor: 'pointer' }}
                    >
                      {task.title}
                    </Anchor>
                    <Text size="sm" c="#888888">
                      {subtaskDetail.title}
                    </Text>
                  </Breadcrumbs>
                  <ActionIcon variant="subtle" size="sm" onClick={onClose} c="dimmed">
                    <X size={16} />
                  </ActionIcon>
                </Group>

                {/* Subtask title + Copy Link + Edit button */}
                <Group justify="space-between" align="flex-start">
                  <Group gap={8} style={{ flex: 1 }} align="center">
                    <Text fw={600} size="xl" c="#28463A">
                      {subtaskDetail.title}
                    </Text>
                    {/* MED-770: Always-visible copy link in drawer */}
                    <Tooltip label="Copy link to task" position="top" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => copyTaskLink(subtaskDetail.id, cohortId)}
                      >
                        <LinkIcon size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Tooltip label="Edit subtask">
                    <ActionIcon
                      variant="light"
                      onClick={() => {
                        const subtaskAsWithDetails = task.subtasks.find(s => s.id === subtaskDetail.id);
                        if (subtaskAsWithDetails) {
                          handleEditSubtaskInPane(subtaskAsWithDetails);
                          setViewingSubtaskId(null);
                          setSubtaskDetail(null);
                        }
                      }}
                    >
                      <Edit3 size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>

                {/* Due date + Priority row */}
                <Group gap="md">
                  {subtaskDetail.due_date && (
                    <Group gap={4}>
                      <Calendar
                        size={14}
                        color={
                          subtaskDetail.due_date &&
                          isDateOnlyPast(subtaskDetail.due_date) &&
                          subtaskDetail.status !== 'complete' &&
                          subtaskDetail.status !== 'cancelled'
                            ? 'var(--mantine-color-dangerRed-6)'
                            : '#555555'
                        }
                      />
                      <Text
                        size="sm"
                        c={
                          subtaskDetail.due_date &&
                          isDateOnlyPast(subtaskDetail.due_date) &&
                          subtaskDetail.status !== 'complete' &&
                          subtaskDetail.status !== 'cancelled'
                            ? 'dangerRed'
                            : '#555555'
                        }
                        fw={
                          subtaskDetail.due_date &&
                          isDateOnlyPast(subtaskDetail.due_date) &&
                          subtaskDetail.status !== 'complete' &&
                          subtaskDetail.status !== 'cancelled'
                            ? 600
                            : 400
                        }
                      >
                        Due {formatDateOnly(subtaskDetail.due_date)}
                        {subtaskDetail.due_date &&
                          isDateOnlyPast(subtaskDetail.due_date) &&
                          subtaskDetail.status !== 'complete' &&
                          subtaskDetail.status !== 'cancelled' &&
                          ' (Overdue)'}
                      </Text>
                    </Group>
                  )}
                  {(subtaskDetail.priority === 'high' || subtaskDetail.priority === 'urgent') && (
                    <Group gap={4}>
                      <AlertTriangle size={14} color="#D64545" />
                      <Text size="sm" c="#D64545" fw={600}>
                        High Priority
                      </Text>
                    </Group>
                  )}
                  {subtaskDetail.priority === 'medium' && (
                    <Group gap={4}>
                      <AlertTriangle size={14} color="#C67A3C" />
                      <Text size="sm" c="#C67A3C" fw={600}>
                        Medium Priority
                      </Text>
                    </Group>
                  )}
                </Group>

                <Divider color="#E8E6E1" />

                {/* Description - always visible with zero state */}
                <SectionHeader>Description</SectionHeader>
                {subtaskDetail.description ? (
                  <Text size="14px" c="#28463A" style={{ whiteSpace: 'pre-wrap' }}>
                    {subtaskDetail.description}
                  </Text>
                ) : (
                  <Text size="14px" c="#888888" fs="italic">
                    No description provided
                  </Text>
                )}

                {/* Status */}
                <SectionHeader>Status</SectionHeader>
                {(() => {
                  const statusCfg = getStatusConfig(subtaskDetail.status);
                  return (
                    <Badge
                      color={statusCfg.color}
                      variant={statusCfg.variant}
                      size="sm"
                      tt="uppercase"
                      styles={{ root: { fontWeight: 600, letterSpacing: '0.3px', fontSize: 10 } }}
                    >
                      {statusCfg.label}
                    </Badge>
                  );
                })()}

                {/* Assigned To */}
                <SectionHeader>Assigned To</SectionHeader>
                <Group gap="sm">
                  <Avatar
                    size="lg"
                    radius="xl"
                    color={subtaskDetail.assignee_name
                      ? getAvatarColor(subtaskDetail.assignee_id || subtaskDetail.assignee_name)
                      : 'medleyGreen'}
                  >
                    {subtaskDetail.assignee_name
                      ? getInitials(subtaskDetail.assignee_name)
                      : '?'}
                  </Avatar>
                  <Stack gap={0}>
                    <Text size="14px" c="#28463A" fw={700}>
                      {subtaskDetail.assignee_name || 'Unassigned'}
                    </Text>
                    {subtaskDetail.assignee_name && (
                      <Text size="12px" c="#888888" fw={600}>
                        {subtaskDetail.job_title_name || 'Team Member'}
                      </Text>
                    )}
                  </Stack>
                </Group>

                {/* Activity section */}
                <SectionHeader>Activity</SectionHeader>
                {subtaskDetail.comments.length > 0 ? (
                  <Stack gap="xs">
                    {subtaskDetail.comments.map((comment) => (
                      <Box key={comment.id} p="sm" style={{ backgroundColor: '#F9F7F4', borderRadius: 6 }}>
                        <Group gap="xs" mb={4}>
                          <Avatar size="xs" radius="xl" src={comment.author_avatar_url}>
                            {comment.author_name?.[0]?.toUpperCase()}
                          </Avatar>
                          <Text size="xs" fw={500} c="#28463A">
                            {comment.author_name}
                          </Text>
                          <Text size="xs" c="#888888">
                            •
                          </Text>
                          <Text size="xs" c="#888888">
                            {formatDate(comment.created_at)}
                          </Text>
                        </Group>
                        <Text size="14px" c="#28463A">{comment.content}</Text>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Text size="14px" c="#28463A">No activity yet</Text>
                )}
              </Stack>
            </ScrollArea>

            {/* Footer */}
            <Box
              p="md"
              style={{
                borderTop: '1px solid var(--mantine-color-gray-2)',
                backgroundColor: 'var(--mantine-color-gray-0)',
              }}
            >
              <Group justify="space-between">
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={handleBackToParent}
                  leftSection={<ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />}
                >
                  Back to Parent Task
                </Button>
                <Button
                  variant={subtaskDetail.status === 'complete' ? 'light' : 'filled'}
                  color="medleyGreen"
                  leftSection={<Check size={16} />}
                  onClick={handleSubtaskMarkComplete}
                >
                  {subtaskDetail.status === 'complete' ? 'Mark Incomplete' : 'Mark Complete'}
                </Button>
              </Group>
            </Box>
          </>
        )
      ) : (
        // View mode - Single scrollable panel
        <>
          <ScrollArea style={{ flex: 1 }} p="lg">
            <Stack gap="md">
              {/* Header: Sub-group label + Close button row */}
              <Group justify="space-between" align="flex-start">
                <Group gap="xs">
                  {task.sub_group && (
                    <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.3px' }}>
                      {task.sub_group}
                    </Text>
                  )}
                  {cohortName && (
                    <Badge size="sm" variant="light" color="medleyGreen" styles={{ root: { fontWeight: 600 } }}>
                      {cohortName}
                    </Badge>
                  )}
                </Group>
                <ActionIcon variant="subtle" size="sm" onClick={onClose} c="dimmed">
                  <X size={16} />
                </ActionIcon>
              </Group>

              {/* Task title + Copy Link + Edit button */}
              <Group justify="space-between" align="flex-start">
                <Group gap={8} style={{ flex: 1 }} align="center">
                  <Text fw={600} size="xl" c="#28463A">
                    {task.title}
                  </Text>
                  {/* MED-770: Always-visible copy link in drawer */}
                  <Tooltip label="Copy link to task" position="top" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => copyTaskLink(task.id, cohortId)}
                    >
                      <LinkIcon size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Tooltip label="Edit task">
                  <ActionIcon
                    variant="light"
                    onClick={() => {
                      if (onEditTask) {
                        onEditTask(task as TaskWithDetails);
                      } else {
                        setEditing(true);
                      }
                    }}
                  >
                    <Edit3 size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              {/* Due date + Priority row */}
              <Group gap="md">
                {task.due_date && (
                  <Group gap={4}>
                    <Calendar size={14} color={isOverdue ? 'var(--mantine-color-dangerRed-6)' : '#555555'} />
                    <Text size="sm" c={isOverdue ? 'dangerRed' : '#555555'} fw={isOverdue ? 600 : 400}>
                      Due {formatDateOnly(task.due_date)}
                      {isOverdue && ' (Overdue)'}
                    </Text>
                  </Group>
                )}
                {(task.priority === 'high' || task.priority === 'urgent') && (
                  <Group gap={4}>
                    <AlertTriangle size={14} color="#D64545" />
                    <Text size="sm" c="#D64545" fw={600}>
                      High Priority
                    </Text>
                  </Group>
                )}
                {task.priority === 'medium' && (
                  <Group gap={4}>
                    <AlertTriangle size={14} color="#C67A3C" />
                    <Text size="sm" c="#C67A3C" fw={600}>
                      Medium Priority
                    </Text>
                  </Group>
                )}
              </Group>

              {/* Border separator below header */}
              <Divider color="#E8E6E1" />

              {/* Related Session Card */}
              {task.session_details && (
                <>
                  <SectionHeader>Related Session</SectionHeader>
                  <RelatedSessionCard session={task.session_details} cohortId={cohortId} onNavigate={onClose} />
                </>
              )}

              {/* Description - always visible with zero state (MED-316) */}
              <SectionHeader>Description</SectionHeader>
              {task.description ? (
                <Text size="14px" c="#28463A" style={{ whiteSpace: 'pre-wrap' }}>
                  {task.description}
                </Text>
              ) : (
                <Text size="14px" c="#888888" fs="italic">
                  No description provided
                </Text>
              )}

              {/* Subtasks — hidden when viewing a subtask (MED-706): subtasks are single-level only */}
              {!task.parent_task_id && (() => {
                // MED-868: Derive the displayed completion count from the effective
                // status of each subtask (external override map wins, then local fetch).
                // This keeps the header counter in sync when subtasks are toggled
                // from the inline dropdown while the drawer is open.
                const effectiveCompletedCount = task.subtasks.reduce((acc, s) => {
                  const status = externalSubtaskStatusMap?.[s.id] ?? s.status;
                  return acc + (status === 'complete' ? 1 : 0);
                }, 0);
                return (
                <>
                  <SectionHeader>
                    Subtasks
                    <Text component="span" size="11px" c="#888888" fw={400} ml="xs">
                      {effectiveCompletedCount} of {task.subtask_count}
                    </Text>
                  </SectionHeader>
                  <Box>
                    {task.subtasks.length > 0 && (
                      <Stack gap="xs" mb="sm">
                        {task.subtasks.map((subtask) => (
                          <SubtaskItem
                            key={subtask.id}
                            subtask={subtask}
                            cohortId={cohortId}
                            parentTaskId={task.id}
                            effectiveStatus={externalSubtaskStatusMap?.[subtask.id] ?? subtask.status}
                            onUpdated={fetchTask}
                            onEdit={handleEditSubtaskInPane}
                            onView={handleViewSubtask}
                            onSubtaskToggled={onSubtaskToggled}
                          />
                        ))}
                      </Stack>
                    )}

                    {/* Add subtask input */}
                    <Group gap="xs">
                      <TextInput
                        placeholder="Add a subtask..."
                        value={newSubtaskTitle}
                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                        style={{ flex: 1 }}
                        size="sm"
                        leftSection={<Plus size={14} />}
                      />
                      <ActionIcon
                        onClick={handleAddSubtask}
                        loading={addingSubtask}
                        disabled={!newSubtaskTitle.trim()}
                        variant="light"
                        color="medleyGreen"
                      >
                        <Plus size={16} />
                      </ActionIcon>
                    </Group>
                  </Box>
                </>
                );
              })()}

              {/* Assigned To */}
              <SectionHeader>Assigned To</SectionHeader>
              <Group gap="sm">
                <Avatar size="lg" radius="xl" color="medleyGreen">
                  {task.assignee_name
                    ? task.assignee_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                    : '?'}
                </Avatar>
                <Stack gap={0}>
                  <Text size="14px" c="#28463A" fw={700}>
                    {task.assignee_name || 'Unassigned'}
                  </Text>
                  {task.assignee_name && (
                    <Text size="12px" c="#888888" fw={600}>
                      {task.job_title_name || 'Team Member'}
                    </Text>
                  )}
                </Stack>
              </Group>

              {/* Activity section */}
              <SectionHeader>Activity</SectionHeader>

              {/* Add comment input (expandable) */}
              {showCommentInput && (
                <Box>
                  <Textarea
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    minRows={2}
                  />
                  <Group justify="flex-end" mt="xs" gap="xs">
                    <Button variant="subtle" size="xs" onClick={() => setShowCommentInput(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      onClick={handleAddComment}
                      loading={addingComment}
                      disabled={!newComment.trim()}
                    >
                      Add Comment
                    </Button>
                  </Group>
                </Box>
              )}

              {/* Show existing comments if any */}
              {task.comments.length > 0 ? (
                <Stack gap="xs">
                  {task.comments.map((comment) => (
                    <Box key={comment.id} p="sm" style={{ backgroundColor: '#F9F7F4', borderRadius: 6 }}>
                      <Group gap="xs" mb={4}>
                        <Avatar size="xs" radius="xl" src={comment.author_avatar_url}>
                          {comment.author_name?.[0]?.toUpperCase()}
                        </Avatar>
                        <Text size="xs" fw={500} c="#28463A">
                          {comment.author_name}
                        </Text>
                        <Text size="xs" c="#888888">
                          •
                        </Text>
                        <Text size="xs" c="#888888">
                          {formatDate(comment.created_at)}
                        </Text>
                      </Group>
                      <Text size="14px" c="#28463A">{comment.content}</Text>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Text size="14px" c="#28463A">No activity yet</Text>
              )}
            </Stack>
          </ScrollArea>

          {/* Footer with action buttons */}
          <Box
            p="md"
            style={{
              borderTop: '1px solid var(--mantine-color-gray-2)',
              backgroundColor: 'var(--mantine-color-gray-0)',
            }}
          >
            <Group justify="space-between">
              <Group gap="xs">
                <Tooltip label="Add comment">
                  <ActionIcon
                    variant={showCommentInput ? 'filled' : 'light'}
                    onClick={() => setShowCommentInput(!showCommentInput)}
                    color={showCommentInput ? 'medleyGreen' : 'gray'}
                  >
                    <MessageSquare size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Attach file (coming soon)">
                  <ActionIcon variant="light" color="gray" disabled>
                    <Paperclip size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <Button
                variant={task.status === 'complete' ? 'light' : 'filled'}
                color="medleyGreen"
                leftSection={<Check size={16} />}
                onClick={handleMarkComplete}
              >
                {task.status === 'complete' ? 'Mark Incomplete' : 'Mark Complete'}
              </Button>
            </Group>
          </Box>
        </>
      )}
    </Drawer>
  );
}

export default TaskDetailDrawer;
